<template>
  <div :class="summaryClass">
    <div class="content-header">
      <h2 v-if="currentMode != 'final'">
        <span>Section Summary:</span>
        {{ sectionName }}
      </h2>
      <h2 v-if="currentMode == 'final'">Your Summary</h2>

      <div>
        <p
          v-if="currentMode != 'final'"
        >Here is a summary of what you shared with us in this section. Do you want to make any changes?</p>
        <p
          v-if="currentMode == 'final'"
        >Here is a summary of what you shared with us. Do you want to make any last changes?</p>
      </div>
      <section v-if="currentMode != 'final'">
        <!--summary first onboarding content -->
        <button @click="showModal">See how to edit this list</button>
        <modal v-show="isModalVisible" @close="closeModal">
          <template v-slot:modalName>Your Question List</template>
          <template v-slot:modalHeaderText>
            <h4>How to edit the list</h4>
            <p>There is a running list of all your questions so far in one box, and all your answers from the section just completed in another box.</p>
            <p>
              <em>
                <strong>When you've gone through all the sections, you'll be able to organize and prioritize your final list before saving and finishing.</strong>
              </em>
            </p>
          </template>
          <template v-slot:modalBody>
            <h4>To modify your list:</h4>
            <div class="grid-section">
              <p>Type directly in the boxes to edit your entries</p>
              <img alt="edit animation" src="/images/change-text.gif" />
            </div>
            <div class="grid-section">
              <p>Press the "Add More" button to add more items to that section</p>
              <img class="still" alt="edit animation" src="/images/add-still.png" />
            </div>
            <div class="grid-section">
              <p>Press the "Remove" button on the right of an item to delete it</p>
              <img class="still" alt="edit animation" src="/images/remove-still.png" />
            </div>

            <div class="grid-section">
              <p>Click and hold the "Move" button on the left of an item to drag it up or down in the list</p>
              <img alt="edit animation" src="/images/move.gif" />
            </div>

            <p>When finished reviewing your list, press the "Save and return to chat" button at the bottom of the page to move to the next section.</p>
          </template>
        </modal>
      </section>
      <section v-if="currentMode == 'final'">
        <!--summary first onboarding content -->
        <button @click="showModal">See how to edit this list</button>
        <modal v-show="isModalVisible" @close="closeModal">
          <template v-slot:modalName>Your Final List</template>

          <template v-slot:modalBody>
            <h4>To modify your list:</h4>
            <div class="grid-section">
              <p>Type directly in the boxes to edit your entries</p>
              <img alt="edit animation" src="/images/change-text.gif" />
            </div>
            <div class="grid-section">
              <p>Press the "Add More" button to add more items to that section</p>
              <img class="still" alt="edit animation" src="/images/add-still.png" />
            </div>
            <div class="grid-section">
              <p>Press the "Remove" button on the right of an item to delete it</p>
              <img class="still" alt="edit animation" src="/images/remove-still.png" />
            </div>

            <div class="grid-section">
              <p>Click and hold the "Move" button on the left of an item to drag it up or down in the list</p>
              <img alt="edit animation" src="/images/move.gif" />
            </div>

            <p>When you are finished with your list, press "Finish and Save" at the bottom of the page.</p>
          </template>
        </modal>
      </section>
    </div>

    <main>
      <article class="questions-list">
        <section :class="questionsClass">
          <h2>My Questions and Concerns</h2>
          <h3>Clarifying what I want to discuss with my doctor</h3>
          <summary-list
            valueKey="QuestionsConcerns"
            :initial="summaryResponses.QuestionsConcerns"
            :removable="true"
          />
        </section>
      </article>

      <article class="topics-list grid-section">
        <section :class="situationClass">
          <h2>My Situation</h2>
          <h3>Clarifying the facts about my condition</h3>
          <h4>My cancer</h4>
          <p
            v-if="summaryResponses.CancerType.question == undefined || summaryResponses.CancerType.question.length == 0"
          >You mentioned that you didn't know your cancer type.</p>
          <summary-field
            valueKey="CancerType"
            :initial="summaryResponses.CancerType"
            :removable="false"
          />
          <h4>Details about my cancer</h4>

          <summary-list
            valueKey="CancerDetails"
            :initial="summaryResponses.CancerDetails"
            :removable="true"
          />
          <h4>My health situation</h4>
          <summary-list
            valueKey="OverallHealth"
            :initial="summaryResponses.OverallHealth"
            :removable="true"
          />
        </section>

        <section :class="optionsClass">
          <h2>My Options</h2>
          <h3>Clarifying my treatment options</h3>
          <h4>My treatment options</h4>
          <p
            v-if="summaryResponses.dontKnowTreatment"
          >You mentioned that you didn't know your treatment options.</p>
          <summary-list
            valueKey="OptionsDetails"
            :initial="summaryResponses.OptionsDetails"
            :removable="true"
          />
        </section>

        <section :class="networkClass">
          <h2>My Support</h2>
          <h3>Clarifying who can help me</h3>
          <h4>My medical network</h4>
          <p
            v-if="summaryResponses.dontKnowMedicalTeam"
          >You mentioned that you didn't know who was on your medical team.</p>
          <summary-list
            valueKey="SupportMedical"
            :initial="summaryResponses.SupportMedical"
            :removable="true"
          />
          <h4>My personal network</h4>
          <p
            v-if="summaryResponses.noPersonalSupport"
          >You mentioned that you didn't know anyone in your personal life that could help you.</p>
          <summary-list
            valueKey="SupportPersonal"
            :initial="summaryResponses.SupportPersonal"
            :removable="true"
          />
        </section>

        <section :class="goalsClass">
          <h2>My Goals and Priorities</h2>
          <h3>Clarifying what's important to me</h3>
          <div v-for="(value,index) in summaryResponses.values" :key="index">
            <h4 v-html="value.question" />
            <summary-list
              :valueKey="'values_list.' + value.question"
              :initial="summaryResponses.values_list[value.question]"
              :removable="true"
            />
          </div>
        </section>
      </article>
    </main>
    <footer class="content-footer">
      <button v-if="nextPage" @click="goToNextPage">Save and return to chat</button>
      <button v-if="currentMode == 'final'" @click="lock">Finish and Save</button>
    </footer>
  </div>
</template>

<style scoped>
.content-footer {
  box-shadow: 0 -5px 7px 0 rgba(0, 0, 0, 0.26);
  display: flex;
  justify-content: center;
}
</style>

<script>
import SummaryField from "@/components/SummaryField.vue";
import SummaryList from "@/components/SummaryList.vue";
import modal from "@/components/modal.vue";
import { mapState, mapMutations } from "vuex";
import store from "@/store";
import { axios } from "@/main.js";

const lstore = store;

export default {
  name: "summary_page",

  props: {
    mode: String // situation, options, network, goals, evaluation, or final,
  },

  beforeRouteEnter(to, from, next) {
    let mode = to.params.mode;
    if (lstore.state.interstitialSummaries.includes(mode)) {
      next(cancel);
    }
    else {
      next();
    }
  },

  data() {
    return {
      currentMode: this.mode,
      isModalVisible: false
    };
  },
  created() {
    this.createSummaryResponses(this.mode);

    this.recordVisit(this.mode);
  },
  computed: {
    ...mapState(["summaryResponses", "chatResponses", "accessCode", "sessionID"]),

    summaryClass() {
      if (this.currentMode == "final") {
        return "summary";
      } else {
        return "summary interstitial";
      }
    },

    sectionName() {
      switch (this.currentMode) {
        case "situation":
          return "My Situation";
          break;

        case "options":
          return "My Options";
          break;

        case "network":
          return "My Network";
          break;

        case "goals":
          return "My Goals";
          break;
      }

      return "";
    },

    situationClass() {
      return this.currentMode == "situation" ? "active" : "";
    },

    optionsClass() {
      return this.currentMode == "options" ? "active" : "";
    },

    networkClass() {
      return this.currentMode == "network" ? "active" : "";
    },

    goalsClass() {
      return this.currentMode == "goals" ? "active" : "";
    },

    questionsClass() {
      return this.currentMode != "final" ? "active" : "";
    },

    nextPage() {
      switch (this.currentMode) {
        case "situation":
          return "options";
          break;

        case "options":
          return "network";
          break;

        case "network":
          return "goals";
          break;

        case "goals":
          return "evaluation";
          break;
      }

      return undefined;
    }
  },

  methods: {
    ...mapMutations(["createSummaryResponses", "setSessionFinalized"]),

    lock() {
      this.setSessionFinalized(new Date());

      this.$router.push("/summary/locked");
    },

    showModal() {
      this.isModalVisible = true;
    },
    closeModal() {
      this.isModalVisible = false;
    },
    goToNextPage() {
      if (this.nextPage) {
        let route = {
          name: "chat-section",
          params: { section: this.nextPage, startID: "new" }
        };
        this.$router.push(route);
      }
    },
    recordVisit(currentMode) {
      let url = "/save_event/" + this.accessCode + "/";
      var formData = new FormData();

      formData.set('event_name', 'Summary_' + currentMode);
      formData.set('note', currentMode + ' summary for session ' + this.sessionID);
      
      axios.post(url, formData);
    }
  },

  components: {
    "summary-field": SummaryField,
    "summary-list": SummaryList,
    modal
  }
};
</script>
